import React from "react";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";

import {
  createStreamSetting,
  updateStreamSetting,
} from "../../../../api/retensionApis";
import { useCourtSessionOptions } from "../../../../hooks/useCourtSessionOptions";
import type { SearchData } from "../../../../types";
import type {
  StreamSettingPayload,
  StreamSettingSelectionLabels,
} from "./StreamSetting.types";
import StreamCourtSelect from "./StreamCourtSelect";
import StreamDelaySelect from "./StreamDelaySelect";

interface BenchStreamSettingsFormProps {
  children?: React.ReactNode;
  editId: string | null;
  formValues: StreamSettingPayload;
  onCancel: () => void;
  onFormValuesChange: (values: StreamSettingPayload) => void;
  onSaved: () => void;
  selectedLabels?: StreamSettingSelectionLabels;
}

const getSaveLabel = (isSaving: boolean, editId: string | null) => {
  if (isSaving) return editId ? "Updating..." : "Saving...";

  return editId ? "Update Streaming" : "Save Streaming";
};

const BenchStreamSettingsForm: React.FC<BenchStreamSettingsFormProps> = ({
  children,
  editId,
  formValues,
  onCancel,
  onFormValuesChange,
  onSaved,
  selectedLabels = {},
}) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const { highCourts, benches, benchRooms, controls } = useCourtSessionOptions(
    toCourtSearchData({
      highcourt: formValues.highcourtId,
      bench: formValues.benchId,
      benchRoom: formValues.benchRoomId || "",
    }),
    "highCourt",
  );

  const setDelay = (delayInMinutes: number | string) => {
    onFormValuesChange({
      ...formValues,
      delayInMinutes,
    });
  };

  const handleSave = async () => {
    if (!formValues.highcourtId) {
      toast.error("Select high court");
      return;
    }

    if (!formValues.benchId) {
      toast.error("Select bench");
      return;
    }

    if (!formValues.benchRoomId) {
      toast.error("Select bench room");
      return;
    }

    const delay = Number(formValues.delayInMinutes);

    if (Number.isNaN(delay)) {
      toast.error("Delay must be a number");
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        courtRoomId: formValues.benchRoomId,
        delayInMinutes: delay,
        links: formValues.links || [],
        courtRoomModel: "BenchRooms",
      };

      if (editId) {
        await updateStreamSetting({ id: editId, ...payload });
        toast.success("Bench stream updated successfully");
      } else {
        await createStreamSetting(payload);
        toast.success("Bench stream saved successfully");
      }

      onSaved();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="p-6 space-y-8 min-h-[45vh]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <StreamCourtSelect
            label="High Court"
            placeholder="Select High Court"
            searchPlaceholder="Search high court..."
            options={highCourts}
            value={formValues.highcourtId}
            paginationControl={controls.highCourts}
            selectedLabel={selectedLabels.highcourt}
            onChange={(highcourtId) => {
              onFormValuesChange({
                ...formValues,
                highcourtId,
                benchId: "",
                benchRoomId: "",
                courtRoomId: "",
                stateId: "",
                districtId: "",
                complexId: "",
                establishmentId: "",
              });
            }}
          />

          <StreamCourtSelect
            label="Bench"
            placeholder="Select Bench"
            searchPlaceholder="Search bench..."
            options={benches}
            value={formValues.benchId}
            disabled={!formValues.highcourtId}
            paginationControl={controls.benches}
            selectedLabel={selectedLabels.bench}
            onChange={(benchId) => {
              onFormValuesChange({
                ...formValues,
                benchId,
                benchRoomId: "",
                courtRoomId: "",
              });
            }}
          />

          <StreamCourtSelect
            label="Bench Rooms"
            placeholder="Select Bench Rooms"
            searchPlaceholder="Search bench room..."
            options={benchRooms}
            value={formValues.benchRoomId || ""}
            disabled={!formValues.benchId}
            paginationControl={controls.benchRooms}
            selectedLabel={selectedLabels.benchRoom}
            onChange={(benchRoomId) => {
              onFormValuesChange({
                ...formValues,
                benchRoomId,
                courtRoomId: benchRoomId,
              });
            }}
          />

          <StreamDelaySelect
            value={formValues.delayInMinutes}
            onChange={setDelay}
          />
        </div>

        {children}
      </div>

      <div className="px-6 py-4 flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-5 py-2 border rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`min-w-28 px-6 py-2 flex items-center justify-center gap-2 rounded-lg text-white transition ${
            isSaving
              ? "bg-blue-400 opacity-70 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
          {getSaveLabel(isSaving, editId)}
        </button>
      </div>
    </>
  );
};

const toCourtSearchData = ({
  highcourt,
  bench,
  benchRoom,
}: {
  highcourt: string;
  bench: string;
  benchRoom: string;
}): SearchData => ({
  cnr: "",
  highCourt: "",
  state: "",
  highcourt,
  bench,
  benchRoom,
  district: "",
  courtComplex: "",
  establishment: "",
  courtName: "",
  date: "",
  status: "all",
});

export default BenchStreamSettingsForm;
